import { useEffect, useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router';
import Alert from 'react-bootstrap/Alert';
import { PageId, type RouteHandle, WordsView } from '../types';
import './SearchPage.scss';
import FlashcardView from '../components/FlashcardView';
import { useAtomValue } from 'jotai';
import { wordsDataAtom, wordsErrorAtom } from '@app/atoms/wordsAtoms';
import Button from 'react-bootstrap/Button';
import WordsListView from '@app/components/WordsListView.tsx';
import CollectionsPanel from '@app/components/CollectionsPanel';
import { CollectionWordsResponse } from '@shared/types/words';

const findCollectionId = (text: string): string | undefined => {
  const uuidV4Regex = /[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;
  const match = text.match(uuidV4Regex);
  return match ? match[0] : undefined;
};

const findView = (text: string): WordsView | undefined => {
  const viewRegex = /\b(cards|list|base)\b/;
  const match = text.match(viewRegex);
  
  if (match) {
    const view = match[1] as WordsView;
    return view;
  }
  
  return undefined;
};

const CollectionPage = () => {
  const { '*': splatParams } = useParams();
  const wordsData = useAtomValue(wordsDataAtom);
  const wordsError = useAtomValue(wordsErrorAtom);
  const [showCards, setShowCards] = useState(false);
  const [showWordsList, setShowWordsList] = useState(false);
  const [collectionId, setCollectionId] = useState<string | undefined>(undefined);
  const [view, setView] = useState<WordsView | undefined>(undefined);

  const { pageId } = useOutletContext<RouteHandle>();

  const navigate = useNavigate();

  useEffect(() => {
    // get only once from url
    console.log('splatParams!!!!!!!!!!!: ', splatParams);
    const _collectionId = findCollectionId(splatParams || '');
    const _view = findView(splatParams || '') || WordsView.CARDS; // default view is cards

    if (_collectionId && _collectionId !== collectionId) {
      setCollectionId(_collectionId);
    }
    if (_view && _view !== view) {
      setView(_view);
    }
  }, []);

  useEffect(() => {
    if (collectionId || view) {
      navigate(`/${PageId.COLLECTION}${collectionId ? '/' + collectionId : ''}${view ? '/' + view : ''}`, { replace: true });
    } else {
      navigate(`/${PageId.COLLECTION}`, { replace: true });
    }
  }, [collectionId, view, navigate]);

  useEffect(() => {
    if (wordsData.words.length > 0) {
      if (view === WordsView.CARDS) {
        setShowCards(true);
      } else if (view === WordsView.LIST) {
        setShowWordsList(true);
      }
    } else {
      setShowCards(false);
      setShowWordsList(false);
    }

    if (wordsData.parts.size > 0) {
      setCollectionId((wordsData.parts.values().next().value as CollectionWordsResponse).collectionId || undefined);
    }
  }, [wordsData]);

  const handleCardsOpen = () => {
    if (wordsData.words.length > 0) {
      setShowCards(true);
      setView(WordsView.CARDS);
    }
  };

  const handleCardsClose = () => {
    setShowCards(false);
    setView(WordsView.BASE);
  };

  const handleListOpen = () => {
    setShowWordsList(true);
    setView(WordsView.LIST);
  };

  const handleWordsListClose = () => {
    setShowWordsList(false);
    setView(WordsView.BASE);
  };

  return (
    <div className="container">
      <h2 className="text-center">Reel Words &amp; Phrases</h2>
      <CollectionsPanel pageId={pageId} collectionId={collectionId} />
      {/* Error Alert */}
      {wordsError && (
        <Alert variant="danger" className="my-5">
          {wordsError}
        </Alert>
      )}
      {wordsData.words.length > 0 && (<div className="actions-panel my-3">
        <Button variant="outline-secondary" className="d-flex w-100 mb-1" onClick={handleCardsOpen}>
          <span className="display-6"><i className="ic ic-cards" style={{height: '3rem', width: '3rem'}} /> Learn important words</span>
        </Button>
        <Button variant="outline-secondary" className="d-flex w-100 mb-1" onClick={handleListOpen}>
          <span className="display-6"><i className="ic ic-long-list" style={{height: '3rem', width: '3rem'}} /> List of words</span>
        </Button>
      </div>)}
      {showCards && (<FlashcardView onClose={handleCardsClose} />)}
      {showWordsList && (<WordsListView wordsData={wordsData.words} collectionId={collectionId} loadMoreEnabled={wordsData.parts.size < wordsData.totalParts} onClose={handleWordsListClose} />)}
    </div>
  );
};

export default CollectionPage;
